import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00` : s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function SpecialPayerTag({ stock }) {
  const [open, setOpen] = useState(false);
  const count = Number(stock?.special_payer_count || 0);
  const rows = Array.isArray(stock?.special_payer_history) ? stock.special_payer_history : [];
  const isBdc = !!stock?.is_bdc;
  const isBrazilAdr = !!stock?.is_adr && String(stock?.country_code || '').toUpperCase() === 'BR';
  if (!stock || stock.pays_dividend === false || stock.special_only || count < 3) return null;
  const structureNote = isBdc
    ? `${stock.ticker} is a Business Development Company. BDCs must pay out at least 90% of taxable income, and when earnings run ahead of the base dividend they often declare "supplemental" dividends that data providers tag as specials. For a BDC a high count usually signals earnings consistently above the base payout — part of the payout structure, not a windfall.`
    : isBrazilAdr
      ? `${stock.ticker} is a Brazilian ADR. Brazilian companies typically split shareholder payments between ordinary dividends and "interest on capital" (juros sobre capital próprio), a tax-advantaged payout that data providers tag as a special dividend. For these names a high count reflects the regular payout structure — stability, not a windfall.`
      : null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/40 hover:bg-amber-500/20 transition-colors"
        title={`${count} special dividends on record — tap for dates`}
        data-testid="special-payer-tag"
      >
        <Sparkles className="w-3 h-3" /> {isBdc ? 'Supplementals' : isBrazilAdr ? 'Interest on capital' : 'Pays specials'} · {count}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="special-payer-modal">
          <DialogHeader>
            <DialogTitle>{stock.ticker} · pays special dividends</DialogTitle>
            <DialogDescription className="text-sm text-foreground/85 pt-2">
              On top of its regular dividend, {stock.ticker} has paid <strong>{count}</strong> one-off special dividend{count === 1 ? '' : 's'} in the
              history we hold. Specials are not part of the yield, payout ratio or growth streak shown here — they are extra cash returns
              the board votes on case by case.
            </DialogDescription>
          </DialogHeader>
          {structureNote && (
            <p className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-foreground/90" data-testid="special-payer-structure-note">
              <strong>{isBdc ? 'BDC note:' : 'Brazilian ADR note:'}</strong> {structureNote}
            </p>
          )}
          <ul className="mt-2 max-h-60 overflow-y-auto text-sm space-y-1" data-testid="special-payer-list">
            {rows.map((r) => (
              <li key={`${r.date}-${r.amount}`} className="flex justify-between border-b border-border/40 py-1">
                <span>{fmtDate(r.date)}</span>
                <span className="font-mono font-semibold">${Number(r.amount).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          {count > rows.length && <p className="text-xs text-muted-foreground mt-2">Showing the {rows.length} most recent of {count}.</p>}
        </DialogContent>
      </Dialog>
    </>
  );
}
